/* Generic horizontal card scroller, driven by Embla Carousel (vendored at
   assets/embla-carousel.umd.js). The custom element and its data-attribute
   contract are unchanged from the hand-rolled version it replaces — sections
   provide [data-scroller-viewport] > [data-scroller-list] plus prev/next
   buttons, a nav wrapper and a live-region status — so section markup and
   the reviews binder keep working.

   Embla owns the physics (drag, momentum, snapping, looping math); this
   adapter owns everything Embla is unopinionated about: arrow disabled
   states, hiding the nav when nothing overflows, the screen-reader status
   line, centring the arrows on the artwork, and reduced motion.

   data-scroller-inactive="(max-width: 749px)" — optional media query where
   the carousel switches off entirely (the product rows stack vertically on
   phones; Embla's transform would fight that layout). */
if (!customElements.get('risify-scroller')) {
  class RisifyScroller extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready || typeof EmblaCarousel === 'undefined') return;
      this.viewport = this.querySelector('[data-scroller-viewport]');
      this.list = this.querySelector('[data-scroller-list]');
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      this.nav = this.querySelector('[data-scroller-nav]');
      this.status = this.querySelector('[data-scroller-status]');
      if (!this.viewport || !this.list) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const options = {
        container: this.list,
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: 1,
        duration: reduced ? 0 : 16,
        watchDrag: (api, event) => this.dragAllowed(api, event),
      };
      if (this.dataset.scrollerInactive) {
        options.breakpoints = {};
        options.breakpoints[this.dataset.scrollerInactive] = { active: false };
      }
      this.embla = EmblaCarousel(this.viewport, options);

      this.prev?.addEventListener('click', () => this.embla.scrollPrev());
      this.next?.addEventListener('click', () => this.embla.scrollNext());
      const sync = () => this.sync();
      this.embla.on('select', sync).on('reInit', sync);

      /* The design centres the arrows on the artwork, not the card — measured,
         not guessed, so it survives any aspect ratio or breakpoint. Media height
         settles when images load, which Embla has no event for; a ResizeObserver
         on the first media element covers it. */
      const media = this.list.querySelector('[data-scroller-media]');
      if (media && 'ResizeObserver' in window) {
        this.mediaObserver = new ResizeObserver(() => {
          const height = media.getBoundingClientRect().height;
          if (height > 0) this.style.setProperty('--scroller-arrow-top', `${Math.round(height / 2)}px`);
        });
        this.mediaObserver.observe(media);
      }

      this.dataset.ready = 'true';
      this.sync();
    }

    /* The reviews binder calls this after it fills or hides slots — slide
       geometry changed under Embla, so it has to re-measure. */
    refresh() {
      this.embla?.reInit();
    }

    /* Used by the theme editor to bring a selected block into view; Embla is
       transform-driven, so scrollIntoView cannot reach a slide. `jump` skips
       the animation. */
    scrollToIndex(index, jump) {
      this.embla?.scrollTo(index, !!jump);
    }

    /* Scrollers can nest — a review card's photo strip sits inside the
       reviews row. A drag that starts on a nested scroller which can actually
       move belongs to it, so the outer row stands still; a nested scroller
       with nothing to scroll refuses the drag instead of rubber-banding while
       its parent moves. Only scrollers marked data-scroller-nested take part,
       so plain rows behave exactly as before. */
    dragAllowed(api, event) {
      if ('scrollerNested' in this.dataset) {
        return api.canScrollPrev() || api.canScrollNext();
      }
      const target = event.target instanceof Element ? event.target : null;
      const nested = target && target.closest('[data-scroller-nested]');
      if (nested && nested !== this && this.contains(nested)) {
        const inner = nested.embla;
        if (inner && (inner.canScrollPrev() || inner.canScrollNext())) return false;
      }
      return true;
    }

    sync() {
      if (!this.embla) return;
      const active = this.embla.internalEngine().options.active;
      if (this.prev) this.prev.disabled = !this.embla.canScrollPrev();
      if (this.next) this.next.disabled = !this.embla.canScrollNext();
      if (this.nav) this.nav.hidden = !active || this.embla.scrollSnapList().length < 2;
      this.report();
    }

    report() {
      if (!this.status) return;
      const total = this.embla.scrollSnapList().length;
      const index = this.embla.selectedScrollSnap();
      const template = this.status.dataset.scrollerStatus || '{current} / {total}';
      this.status.textContent = template.replace('{current}', index + 1).replace('{total}', total);
    }

    disconnectedCallback() {
      this.mediaObserver?.disconnect();
      this.embla?.destroy();
      delete this.dataset.ready;
    }
  }

  customElements.define('risify-scroller', RisifyScroller);
}
